import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import ModalMoreInfo from './ModalMoreInfo';
import { Text4, Text4B } from '../genericComponents/texts';
import { DISCARDED_PUBLICATION_REASONS } from '../data/discardedPublicationReasons';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function DiscardedPublicationsReasonsModal({ open, onClose }: Readonly<Props>) {
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const modal = (
    <Overlay role="presentation" onClick={onClose}>
      <Panel
        role="dialog"
        aria-modal="true"
        aria-label="Motivos de publicaciones descartadas"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalMoreInfo setVisible={onClose} textButton="Motivos de publicaciones descartadas">
          <PaddingContainer>
            <Text4>
              Algunas publicaciones no se consideran válidas para el cálculo de un precio justo de mercado debido a los siguientes motivos:
            </Text4>
          </PaddingContainer>
          <ModalBody>
            <Table>
              <thead>
                <tr style={{ background: '#F6F6F6' }}>
                  <Th><Text4B>Motivo</Text4B></Th>
                  <Th><Text4B>Descripción</Text4B></Th>
                </tr>
              </thead>
              <tbody>
                {DISCARDED_PUBLICATION_REASONS.map((row) => (
                  <tr key={row.commercialName}>
                    <TdName>
                      <Text4 style={{ color: '#0B3254', fontWeight: 700 }}>{row.commercialName}</Text4>
                    </TdName>
                    <TdDesc>
                      <Text4 style={{ color: '#3D5176' }}>{row.description}</Text4>
                    </TdDesc>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ModalBody>
        </ModalMoreInfo>
      </Panel>
    </Overlay>
  );

  return createPortal(modal, document.body);
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Panel = styled.div`
  background: #ecf1f5;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const ModalBody = styled.div`
  padding: 15px 16px 0px;
  overflow-y: auto;
  max-height: calc(90vh - 140px);
  background: #ecf1f5;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 8px;
`;

const Th = styled.th`
  background: #F6F6F6;
  padding: 12px 10px;
  text-align: left;
  color: #0B3254;
  font-weight: 700;
  font-size: 12px;
  border: 1px solid #E5E5E5;
  vertical-align: top;
`;

const TdName = styled.td`
  padding: 12px 10px;
  border: 1px solid #E5E5E5;
  vertical-align: top;
  width: 32%;
  background: #fff;
`;

const TdDesc = styled.td`
  padding: 12px 10px;
  border: 1px solid #E5E5E5;
  vertical-align: top;
  background: #fff;
`;

const PaddingContainer = styled.div`
  padding: 20px 16px 0px;
`;
